import type { Patient } from 'fhir/r4';
import * as fhirpath from 'fhirpath';
import { useEffect, useMemo } from 'react';
import React from 'react';
import { Loader, Message, Card } from 'semantic-ui-react';
import useSWR from 'swr';
import type { PatientEntry } from 'types';

import { useAuthContext } from '../../../../contexts/AuthContext';
import FHIRFetcher from '../../../../data/FHIRFetcher';
import type { ApiFetchError } from '../../../../services/api-client';
import MemberCard from '../card';

import {
  normalizeSearchString,
  buildSearchParam,
  commonSWROptions,
  type PatientSearchResult,
} from './members-list-utils';

interface NewMembersListProps {
  payer: string;
  searchString: string;
  selectedEligibilityGroup?: string | null;
  refresh: boolean;
  setRefresh: (value: boolean) => void;
  onPatientSelect: (patient: Patient) => void;
}

function NewMembersList(props: NewMembersListProps) {
  const authInfo = useAuthContext();
  const [patientGroups, setPatientGroups] = React.useState<PatientEntry[]>([]);

  // State variabel with array of  PatientIds..ths will be boiund to the SWR query that gets My pateimts
  const handlePatientSelect = (selectedPatient: Patient) => {
    props.onPatientSelect(selectedPatient);
  };

  const normalizedSearchString = useMemo(() => normalizeSearchString(props.searchString), [props.searchString]);

  const options = useMemo(
    () => ({
      ...commonSWROptions,
      onSuccess: (data: PatientSearchResult) => {
        const tempPatient: PatientEntry[] = [];
        //TODO: move this to fhir-proxy later
        //filter out the patient data that doesn't meet the criteria
        if (data && data?.entry?.length > 0) {
          for (let i = 0; i < data?.entry?.length; i++) {
            // In range by the loop bound; the assertion satisfies
            // `noUncheckedIndexedAccess` without touching the optional chain.
            const patient = data?.entry[i] as PatientEntry;

            const patientEligibilityGroups = fhirpath.evaluate(
              patient,
              "resource.identifier.where(system = 'https://projectwell.io/fhir/identifiers/eligibility-group').value"
            );
            if (props?.selectedEligibilityGroup && patientEligibilityGroups?.includes(props.selectedEligibilityGroup)) {
              tempPatient.push(patient);
            } else if (!props?.selectedEligibilityGroup) {
              tempPatient.push(patient);
            }
          }
        }
        setPatientGroups(tempPatient);
      },
    }),
    [props?.selectedEligibilityGroup]
  );

  const key = useMemo(() => {
    const searchParam = buildSearchParam(normalizedSearchString);

    if (props.payer === 'ALL') {
      return `/Patient?identifier=UN-ASSIGNED&identifier:not=INACTIVE&_count=1000${searchParam}`;
    } else {
      return `/Patient?identifier=UN-ASSIGNED&identifier:not=INACTIVE&_count=1000&organization=${props.payer}${searchParam}`;
    }
  }, [props.payer, normalizedSearchString]);

  const {
    data: _patientData,
    error: patientError,
    isValidating: patientsDataLoading,
    mutate: patientsDataMutate,
  } = useSWR<PatientSearchResult, ApiFetchError>([key, authInfo.token], FHIRFetcher, options);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
    patientsDataMutate();
  }, [props?.selectedEligibilityGroup, patientsDataMutate]);

  useEffect(() => {
    if (props.refresh === true) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget SWR revalidation; a rejection only leaves the view stale
      patientsDataMutate();
      props.setRefresh(false);
    }
  }, [props.refresh, patientsDataMutate, props.setRefresh]);

  return (
    <div>
      <p />

      {patientsDataLoading && <Loader active inline="centered" content="Loading new members..." />}

      {patientGroups && patientGroups.length > 0 && (
        <Card.Group>
          {patientGroups.map((patientEntry) => {
            return (
              <MemberCard
                key={patientEntry.resource.id}
                patient={patientEntry.resource}
                onPatientSelect={handlePatientSelect}
              />
            );
          })}
        </Card.Group>
      )}

      {!patientsDataLoading && patientGroups && patientGroups.length === 0 && (
        <Message info>
          <Message.Header>No New Members</Message.Header>
          <p>Looks like there are no New Members</p>
        </Message>
      )}

      {patientError && (
        <Message negative>
          <Message.Header>Oops!. Something went wrong.. </Message.Header>
          {/* SUSPECTED DEFECT: renders empty on the string-rejection branch. See `ApiFetchError`. */}
          <p>{(patientError as { message?: string }).message}</p>
        </Message>
      )}
    </div>
  );
}

export default NewMembersList;
